/**
 * John Paul the Great Academy — Design Tokens (Single Source of Truth)
 *
 * These tokens define the visual DNA of all JPG Academy applications.
 * Every color, spacing value, and typographic scale derives from here.
 *
 * School Identity:
 *   Full name:  John Paul the Great Academy
 *   Short name: JPG Academy
 *   Motto:      A Catholic, Classical School for Acadiana
 *   Crest:      src/assets/crest.png
 *
 * Brand Colors:
 *   Navy (#1B2A4A)    — authority, tradition, depth (primary)
 *   Maroon (#8B1A2B)  — passion, dignity, ecclesial warmth (secondary)
 *   Cream (#F5F0E8)   — parchment, illumination, warmth (accent)
 *
 * Typography:
 *   Trajan Pro Bold (all-caps) — titles, institutional voice
 *   Garamond                     — subtitles & body copy, elegant readability
 *   System sans-serif            — UI chrome, labels, data
 *
 * Design Philosophy:
 *   Classical Catholic academy meets modern utility.
 *   Roman gravitas with Louisiana warmth.
 *   Institutional authority without coldness.
 */

// ─── Brand Identity ──────────────────────────────────────────────────────────

/** School identity tokens — use these everywhere, never hardcode */
export const school = {
	/** John Paul the Great Academy */
	fullName: 'John Paul the Great Academy',
	/** JPG Academy */
	shortName: 'JPG Academy',
	/** A Catholic, Classical School for Acadiana */
	motto: 'A Catholic, Classical School for Acadiana',
	/**
	 * Path to the school crest (background-removed PNG).
	 * Copy to your app's public directory or reference via your bundler.
	 */
	crestSrc: '../assets/crest.png',
} as const

// ─── Color Tokens ────────────────────────────────────────────────────────────

/** Core brand palette in HSL */
export const brand = {
	/** Navy blue — the dominant institutional color */
	navy: {
		hex: '#1B2A4A',
		hsl: '219 47% 20%',
		rgb: '27 42 74',
	},
	/** Maroon/burgundy — warmth, passion, ecclesial dignity */
	maroon: {
		hex: '#8B1A2B',
		hsl: '353 68% 32%',
		rgb: '139 26 43',
	},
	/** Cream/parchment — illumination, warmth, classical paper */
	cream: {
		hex: '#F5F0E8',
		hsl: '38 44% 93%',
		rgb: '245 240 232',
	},
	/** Gold accent — used sparingly for highlights and sacred touches */
	gold: {
		hex: '#C4972A',
		hsl: '41 65% 47%',
		rgb: '196 151 42',
	},
} as const

/** Navy scale — derived from the primary navy */
export const navy = {
	950: { hex: '#0D1526', hsl: '219 49% 10%', rgb: '13 21 38' },
	900: { hex: '#111D35', hsl: '219 49% 13%', rgb: '17 29 53' },
	800: { hex: '#162840', hsl: '219 47% 17%', rgb: '22 40 64' },
	700: { hex: '#1B2A4A', hsl: '219 47% 20%', rgb: '27 42 74' },  // brand navy
	600: { hex: '#243A5C', hsl: '219 42% 26%', rgb: '36 58 92' },
	500: { hex: '#2F4F73', hsl: '219 37% 32%', rgb: '47 79 115' },
	400: { hex: '#4A6D97', hsl: '219 35% 43%', rgb: '74 109 151' },
	300: { hex: '#7A9ABD', hsl: '219 33% 60%', rgb: '122 154 189' },
	200: { hex: '#A9BDD4', hsl: '219 30% 74%', rgb: '169 189 212' },
	100: { hex: '#D4DEE9', hsl: '219 25% 87%', rgb: '212 222 233' },
	50: { hex: '#EEF1F5', hsl: '219 20% 95%', rgb: '238 241 245' },
} as const

/** Maroon scale — derived from the secondary maroon */
export const maroon = {
	950: { hex: '#3A0810', hsl: '353 75% 13%', rgb: '58 8 16' },
	900: { hex: '#520D18', hsl: '353 72% 18%', rgb: '82 13 24' },
	800: { hex: '#6B1220', hsl: '353 70% 24%', rgb: '107 18 32' },
	700: { hex: '#8B1A2B', hsl: '353 68% 32%', rgb: '139 26 43' },  // brand maroon
	600: { hex: '#A62535', hsl: '353 62% 40%', rgb: '166 37 53' },
	500: { hex: '#C13344', hsl: '353 56% 48%', rgb: '193 51 68' },
	400: { hex: '#D65563', hsl: '353 56% 59%', rgb: '214 85 99' },
	300: { hex: '#E07E89', hsl: '353 54% 69%', rgb: '224 126 137' },
	200: { hex: '#EDACB3', hsl: '353 55% 79%', rgb: '237 172 179' },
	100: { hex: '#F7D4D8', hsl: '353 60% 90%', rgb: '247 212 216' },
	50: { hex: '#FEF0F1', hsl: '353 75% 97%', rgb: '254 240 241' },
} as const

/** Cream scale — warm neutrals derived from the accent cream.
 *
 * Unlike navy and maroon, cream is inherently light, so the scale is compressed.
 * The **brand cream still lives at 700** to match the system convention that
 * brand colors sit at the 700 step.
 */
export const cream = {
	950: { hex: '#B5ADA3', hsl: '38 10% 67%', rgb: '181 173 163' },
	900: { hex: '#D8D2C8', hsl: '38 20% 81%', rgb: '216 210 200' },
	800: { hex: '#EDE9E1', hsl: '38 35% 91%', rgb: '237 233 225' },
	700: { hex: '#F5F0E8', hsl: '38 44% 93%', rgb: '245 240 232' },  // brand cream
	600: { hex: '#F7F3ED', hsl: '38 40% 95%', rgb: '247 243 237' },
	500: { hex: '#F8F5F0', hsl: '38 36% 96%', rgb: '248 245 240' },
	400: { hex: '#FBF9F5', hsl: '38 40% 98%', rgb: '251 249 245' },
	300: { hex: '#FCFBF8', hsl: '38 35% 98%', rgb: '252 251 248' },
	200: { hex: '#FDFCFA', hsl: '38 30% 99%', rgb: '253 252 250' },
	100: { hex: '#FEFDFB', hsl: '38 25% 99%', rgb: '254 253 251' },
	50: { hex: '#FFFDFC', hsl: '38 20% 99%', rgb: '255 253 252' },
} as const

/** Semantic color tokens mapped to brand palette */
export const semantic = {
	primary: brand.navy,
	secondary: brand.maroon,
	accent: brand.cream,

	/** Success — verdant green, growth, approval */
	success: { hex: '#2D6A4F', hsl: '153 39% 30%', rgb: '45 106 79' },
	/** Warning — amber, attention needed */
	warning: { hex: '#B07D10', hsl: '42 84% 38%', rgb: '176 125 16' },
	/** Error/danger — deep red, immediate attention */
	error: { hex: '#9B2226', hsl: '359 64% 37%', rgb: '155 34 38' },
	/** Info — navy-tinted blue, informational */
	info: { hex: '#2A6F97', hsl: '204 57% 37%', rgb: '42 111 151' },
} as const

// ─── Typography Tokens ──────────────────────────────────────────────────────

export const fonts = {
	/**
	 * Trajan Pro Bold — classical Roman inscription for titles.
	 *
	 * This is a single-weight face (Bold/700). The design system always
	 * renders it at font-weight: 700 with text-transform: uppercase.
	 * There is no "regular" weight — if it's a title, it's Bold.
	 */
	display: {
		family: "'TrajanPro-Bold', 'TrajanPro', 'Cinzel', Georgia, 'Times New Roman', serif",
		weight: 700 as const,
		/** Trajan is all-caps by design — always use text-transform */
		transform: 'uppercase' as const,
		/** Tracking values for all-caps Trajan Pro Bold */
		letterSpacing: {
			tight: '0.04em',
			normal: '0.08em',
			wide: '0.15em',
			widest: '0.25em',
		},
	},

	/**
	 * Garamond — elegant serif for subtitles and body copy.
	 *
	 * Used for all readable prose: subtitles beneath Trajan titles,
	 * paragraph text, descriptions, and supporting content.
	 * Multiple weights give flexibility from light emphasis to bold statements.
	 */
	body: {
		family: "Garamond, 'EB Garamond', 'Cormorant Garamond', 'Palatino Linotype', Georgia, serif",
		weight: {
			light: 300,
			regular: 400,
			medium: 500,
			semibold: 600,
			bold: 700,
		},
	},

	/** System sans-serif — UI chrome, labels, navigation, data */
	ui: {
		family: "system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
		weight: {
			normal: 400,
			medium: 500,
			semibold: 600,
			bold: 700,
		},
	},

	/** Monospace — code, IDs, technical data */
	mono: {
		family: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace",
		weight: {
			normal: 400,
			medium: 500,
			semibold: 600,
		},
	},
} as const

/**
 * Type scale — inspired by classical proportions.
 * Uses a 1.25 (major third) ratio for harmony.
 * Base: 16px / 1rem
 *
 * Hierarchy:
 *   Title (Trajan Pro Bold, uppercase) → Subtitle (Garamond semibold) → Body (Garamond) → UI (system sans)
 */
export const typeScale = {
	/** Display — page heroes, application titles (Trajan Pro Bold) */
	display: {
		size: '3.5rem',      // 56px
		lineHeight: '1.1',
		letterSpacing: '0.04em',
		font: 'display' as const,
		weight: 700 as const,
	},
	/** H1 — section titles, page headings (Trajan Pro Bold) */
	h1: {
		size: '2.5rem',      // 40px
		lineHeight: '1.15',
		letterSpacing: '-0.01em',
		font: 'display' as const,
		weight: 700 as const,
	},
	/** H2 — subsections, card titles (Trajan Pro Bold) */
	h2: {
		size: '1.75rem',     // 28px
		lineHeight: '1.2',
		letterSpacing: '0em',
		font: 'display' as const,
		weight: 700 as const,
	},
	/** H3 — group headings, sidebar titles (Trajan Pro Bold) */
	h3: {
		size: '1.25rem',     // 20px
		lineHeight: '1.3',
		letterSpacing: '0em',
		font: 'display' as const,
		weight: 700 as const,
	},
	/** H4 — minor headings within groups (system sans, semibold) */
	h4: {
		size: '1.125rem',    // 18px
		lineHeight: '1.3',
		letterSpacing: '0em',
		font: 'ui' as const,
		weight: 'semibold' as const,
	},

	/** Subtitle — Garamond semibold, sits beneath a Trajan title */
	subtitle: {
		size: '1.125rem',    // 18px
		lineHeight: '1.5',
		letterSpacing: '0.01em',
		font: 'body' as const,
		weight: 'semibold' as const,
	},
	/** Subtitle small — compact Garamond subtitle */
	'subtitle-sm': {
		size: '1rem',        // 16px
		lineHeight: '1.5',
		letterSpacing: '0em',
		font: 'body' as const,
		weight: 'medium' as const,
	},

	/** Body large — lead paragraphs, introductions (Garamond) */
	'body-lg': {
		size: '1.125rem',    // 18px
		lineHeight: '1.6',
		letterSpacing: '0em',
		font: 'body' as const,
		weight: 'regular' as const,
	},
	/** Body — default text (Garamond) */
	body: {
		size: '1rem',        // 16px
		lineHeight: '1.6',
		letterSpacing: '0em',
		font: 'body' as const,
		weight: 'regular' as const,
	},
	/** Body small — secondary text, descriptions (Garamond) */
	'body-sm': {
		size: '0.875rem',    // 14px
		lineHeight: '1.5',
		letterSpacing: '0em',
		font: 'body' as const,
		weight: 'regular' as const,
	},
	/** Caption — labels, timestamps, metadata (system sans) */
	caption: {
		size: '0.75rem',     // 12px
		lineHeight: '1.4',
		letterSpacing: '0.01em',
		font: 'ui' as const,
		weight: 'medium' as const,
	},

	/** Overline — section labels, categories (always uppercase, system sans) */
	overline: {
		size: '0.6875rem',   // 11px
		lineHeight: '1.3',
		letterSpacing: '0.12em',
		font: 'ui' as const,
		weight: 'semibold' as const,
		transform: 'uppercase' as const,
	},
} as const

// ─── Spacing Tokens ──────────────────────────────────────────────────────────

/**
 * 4px base grid. Named for clarity, not mystery.
 */
export const space = {
	0: '0',
	0.5: '0.125rem',  // 2px
	1: '0.25rem',     // 4px
	1.5: '0.375rem',  // 6px
	2: '0.5rem',      // 8px
	3: '0.75rem',     // 12px
	4: '1rem',        // 16px
	5: '1.25rem',     // 20px
	6: '1.5rem',      // 24px
	8: '2rem',        // 32px
	10: '2.5rem',     // 40px
	12: '3rem',       // 48px
	16: '4rem',       // 64px
	20: '5rem',       // 80px
	24: '6rem',       // 96px
} as const

// ─── Border Radius Tokens ───────────────────────────────────────────────────

export const radius = {
	none: '0',
	sm: '0.25rem',     // 4px — subtle rounding
	md: '0.375rem',    // 6px — buttons, inputs
	lg: '0.5rem',      // 8px — cards, modals
	xl: '0.75rem',     // 12px — large containers
	'2xl': '1rem',     // 16px — hero sections
	full: '9999px',    // pills, avatars
} as const

// ─── Shadow Tokens ──────────────────────────────────────────────────────────

/**
 * Shadows use warm tones (slight amber tint) to match the cream/navy palette
 * rather than cold blue-black defaults.
 */
export const shadows = {
	/** Subtle lift — cards at rest */
	sm: '0 1px 2px 0 rgb(27 42 74 / 0.05)',
	/** Standard elevation — dropdowns, popovers */
	md: '0 4px 6px -1px rgb(27 42 74 / 0.08), 0 2px 4px -2px rgb(27 42 74 / 0.05)',
	/** High elevation — modals, dialogs */
	lg: '0 10px 15px -3px rgb(27 42 74 / 0.08), 0 4px 6px -4px rgb(27 42 74 / 0.04)',
	/** Maximum elevation — full-screen overlays */
	xl: '0 20px 25px -5px rgb(27 42 74 / 0.08), 0 8px 10px -6px rgb(27 42 74 / 0.04)',
	/** Inner shadow — inset fields */
	inner: 'inset 0 2px 4px 0 rgb(27 42 74 / 0.05)',
	/** Warm glow — focus rings, highlights */
	warm: '0 0 0 3px rgb(196 151 42 / 0.3)',
} as const

// ─── Motion Tokens ──────────────────────────────────────────────────────────

export const motion = {
	duration: {
		instant: '100ms',
		fast: '200ms',
		normal: '300ms',
		slow: '500ms',
		graceful: '700ms',
	},
	easing: {
		/** Snappy, responsive — buttons, toggles */
		default: 'cubic-bezier(0.4, 0, 0.2, 1)',
		/** Gentle entrance — modals, panels */
		enter: 'cubic-bezier(0, 0, 0.2, 1)',
		/** Smooth exit — dismissals, collapses */
		exit: 'cubic-bezier(0.4, 0, 1, 1)',
		/** Springy — playful interactions */
		bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
		/** Declarative — key moments, reveals */
		solemn: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
	},
} as const

// ─── Layout Tokens ──────────────────────────────────────────────────────────

export const layout = {
	maxWidth: {
		site: '1400px',
		content: '960px',
		narrow: '640px',
		form: '480px',
	},
	sidebar: {
		width: '280px',
		collapsed: '64px',
	},
	header: {
		height: '64px',
	},
} as const

// ─── Z-Index Scale ──────────────────────────────────────────────────────────

export const zIndex = {
	base: 0,
	dropdown: 10,
	sticky: 20,
	fixed: 30,
	overlay: 40,
	modal: 50,
	popover: 60,
	toast: 70,
	tooltip: 80,
} as const

// ─── Complete Token Map (for JSON export / design tools) ────────────────────

export const tokens = {
	school,
	brand,
	navy,
	maroon,
	cream,
	semantic,
	fonts,
	typeScale,
	space,
	radius,
	shadows,
	motion,
	layout,
	zIndex,
} as const

export default tokens
